import { buildSaleUnitConverter, fromKg, toPricePerKg } from 'modules/shrimpSales/utils/saleUnit'

export interface TransferEstimate {
  /** Estimated number of animals moved. */
  amount: number
  /** Estimated cost of the moved animals (baht). */
  value: number
}

interface TransferEstimateInput {
  isFish: boolean
  weightKg: number
  /** shrimp: pcs/kg, fish: g/pc */
  size: number
  /** shrimp: baht/kg, fish: baht/pc */
  cost: number
}

/**
 * DISPLAY-ONLY estimate per DOF-1368 AC 11/12 (accepted by product): shown
 * on screen so the user can sanity-check a transfer. It is never sent to the
 * backend, which stays the only place that computes money. Reuses the sales unit helpers:
 * shrimp size is pcs/kg, fish size is g/pc.
 */
export const estimateTransfer = ({
  isFish,
  weightKg,
  size,
  cost,
}: TransferEstimateInput): TransferEstimate | null => {
  if (![weightKg, size, cost].every((n) => Number.isFinite(n)) || weightKg <= 0 || cost < 0) {
    return null
  }
  const converter = buildSaleUnitConverter(!isFish, size, '')
  const amount = fromKg(weightKg, 'piece', converter)
  const pricePerKg = toPricePerKg(cost, isFish ? 'piece' : 'kg', converter)
  if (amount === null || pricePerKg === null) return null
  return { amount, value: pricePerKg * weightKg }
}

const ERROR_CODES = [
  'DESTINATION_POND_NOT_AVAILABLE',
  'NOT_ENOUGH_SHRIMP',
  'DESTINATION_POND_SPECIES_MISMATCH',
  'DESTINATION_FARM_NOT_FOUND',
  'DESTINATION_FARM_NOT_IN_ORGANIZATION',
  'NO_PERMISSION_ON_DESTINATION_FARM',
] as const

export type TransferErrorCode = (typeof ERROR_CODES)[number]

/** Backend message ends with the code, e.g. "...: NOT_ENOUGH_SHRIMP". */
export const parseTransferErrorCode = (message?: string): TransferErrorCode | null => {
  const code = (message ?? '').split(':').pop()?.trim()
  return ERROR_CODES.find((c) => c === code) ?? null
}

interface CrossFarmFields {
  source_farm_id?: string
  destination_farm_id?: string
}

/** True when the transfer moved shrimp between two different farms. */
export const isCrossFarmTransfer = (record: CrossFarmFields) =>
  !!record.source_farm_id &&
  !!record.destination_farm_id &&
  record.source_farm_id !== record.destination_farm_id

interface NetRemainingInput {
  /** Stocked count already net of top-ups and move-outs (dashboard `initialShrimpCount`). */
  initialShrimpCount?: number | null
  accumulatedDeadShrimpCount?: number | null
  totalSoldCount?: number | null
}

/**
 * Animals that can still be moved out: net stocked - dead - sold, the same
 * count the backend checks a transfer against (NOT_ENOUGH_SHRIMP). Unlike the
 * dashboard `remainingCount`, which is estimated from feed. Returns undefined
 * until the dashboard has a stocking figure.
 */
export const netRemainingCount = (dashboard?: NetRemainingInput | null): number | undefined => {
  if (typeof dashboard?.initialShrimpCount !== 'number') return undefined
  const net =
    dashboard.initialShrimpCount -
    (dashboard.accumulatedDeadShrimpCount ?? 0) -
    (dashboard.totalSoldCount ?? 0)
  return net <= 0 ? 0 : Math.round(net)
}
